import {
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  RadioGroup,
  Radio
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { Fragment, useEffect, useState } from 'react';
import { cloneDeep } from 'lodash';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { chipStyles } from '../../../utils/helpers';
import { TextBlock } from '../../Atoms/Text';
import { Text } from '../../Atoms/Text';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { Run, RunStatus, updateRun } from '../../../store/Run';

export interface DialogParams {
  run: Run | null;
  open: boolean;
  onClose: () => void;
}

const radioStyle = { height: '2rem', width: '2rem', m: '0 0.3rem' };

export const EditRunDialog = ({ run, open, onClose }: DialogParams) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [newStatus, setNewStatus] = useState<RunStatus>(
    run && run.status === RunStatus.PROCESSING ? RunStatus.PENDING_LOCK : RunStatus.PROCESSING
  );

  const { setSnack } = useSnackbar();

  const handleClose = () => {
    onClose();
  };

  const handleSubmitForm = async () => {
    if (!run) return;
    const clonedRun = cloneDeep(run);
    clonedRun.status = newStatus;
    setIsSubmitting(true);
    const { data } = await updateRun(clonedRun);
    if (data) {
      setSnack({ message: 'Run updated successfully', type: 'success', open: true });
      handleClose();
    }
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (!run) return;
    setNewStatus(run.status === RunStatus.PROCESSING ? RunStatus.PENDING_LOCK : RunStatus.PROCESSING);
  }, [run]);

  if (!run) return null;

  const getNewStatusValue = () => {
    if (run.status === RunStatus.PROCESSING) {
      return (
        <Chip
          label={RunStatus.PENDING_LOCK}
          size="small"
          sx={chipStyles(RunStatus.PENDING_LOCK)}
          title={RunStatus.PENDING_LOCK}
        />
      );
    }
    return (
      <Fragment>
        <RadioGroup
          aria-label="status"
          name="status"
          value={newStatus}
          onChange={(e) => {
            const val = e.target.value as RunStatus;
            setNewStatus(val);
          }}
          className="!m-0 p-0">
          <FormControlLabel
            value={RunStatus.PROCESSING}
            control={<Radio sx={radioStyle} />}
            label={
              <Chip
                label={RunStatus.PROCESSING}
                size="small"
                sx={chipStyles(RunStatus.PROCESSING)}
              />
            }
          />
          <FormControlLabel
            value={RunStatus.LOCKED}
            control={<Radio sx={radioStyle} />}
            label={
              <Chip
                label={RunStatus.LOCKED}
                size="small"
                sx={chipStyles(RunStatus.LOCKED)}
              />
            }
          />
        </RadioGroup>
        {newStatus === RunStatus.LOCKED && (
          <Text
            size="sm14"
            className="mt-2">
            <InfoIcon sx={{ fontSize: 22, color: '#606060', margin: '5px 4px 5px -2px' }} />A Locked run cannot be
            changed back to Pending Lock or Processing.
          </Text>
        )}
      </Fragment>
    );
  };

  return (
    <Dialog
      data-testid="run-status-change-dialog"
      maxWidth="md"
      open={open}
      onClose={handleClose}>
      <DialogTitle>
        <TextBlock
          weight="bold"
          size="lg"
          color="grey9">
          Run Status Change
        </TextBlock>
      </DialogTitle>
      <DialogContent
        sx={{
          minHeight: 70,
          minWidth: 570,
          paddingLeft: 6,
          paddingRight: 6,
          display: 'flex flex-col',
          alignItems: 'center',
          justifyContent: 'left',
          overflowY: 'visible'
        }}>
        <Text
          dataTestId="run-date-label-field"
          size="sm14"
          color="grey6">
          Run
        </Text>
        <Text
          dataTestId="run-date-value"
          size="sm14">
          {run.name}
        </Text>
        <Text
          dataTestId="run-status-from-label-field"
          className="mt-2 mb-1"
          size="sm14"
          color="grey6">
          From
        </Text>
        <Text
          dataTestId="run-status-from-value"
          size="sm14">
          <Chip
            label={run.status}
            size="small"
            sx={chipStyles(run.status)}
            title={run.status}
          />
        </Text>
        <Text
          dataTestId="run-status-to-label-field"
          className="mt-2 mb-1"
          size="sm14"
          color="grey6">
          To
        </Text>
        <Text
          dataTestId="run-status-to-value"
          size="sm14">
          {getNewStatusValue()}
        </Text>
      </DialogContent>
      <hr className="mb-1 mx-6" />
      <DialogActions sx={{ marginRight: 2, pb: 2 }}>
        <button
          className={DEFAULT_CANCEL_BUTTON_STYLE}
          onClick={handleClose}>
          Cancel
        </button>
        {isSubmitting ? (
          <div className="flex">
            <ComponentSpinner
              sx={{ mt: '0', m: '0' }}
              label="Saving..."
            />
          </div>
        ) : (
          <button
            data-testid="submit-confirmation"
            onClick={handleSubmitForm}
            className={DEFAULT_ADVANCE_BUTTON_STYLE}
            type="submit">
            Save
          </button>
        )}
      </DialogActions>
    </Dialog>
  );
};
